// btw-front/src/components/deal/AdoptionFilesSection.jsx
// 도입 증빙 파일 섹션 — 딜 상세(파트너/관리자 공용). 목록 다운로드 + (권한 시) 추가/삭제.
// 교체 권한(canManage)은 부모가 판정: 도입 처리한 본인(adoptedByUserId / adoptedByAdminId)만.
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { message, Modal } from "antd";
import {
  adoptionAccept,
  formatFileSize,
  normalizeAdoptionLimits,
  validateAdoptionFiles,
} from "./AdoptionFilePicker";

/**
 * props
 *  - files: [{ id, originalName, sizeBytes, createdAt }]
 *  - canManage: boolean
 *  - isAdoptionStage: boolean — 도입 상태면 마지막 1개 삭제 버튼 비활성(서버 400 과 일치)
 *  - onDownload(file) / onAdd(File[]) / onRemove(file) — Promise 반환
 *  - limits: 서버 응답 adoptionFileLimits (없으면 기본값)
 *  - className
 */
export default function AdoptionFilesSection({
  files,
  canManage,
  isAdoptionStage,
  onDownload,
  onAdd,
  onRemove,
  limits,
  className = "",
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const list = files || [];
  const lim = normalizeAdoptionLimits(limits);

  async function handleAdd(fileList) {
    const { ok, error } = validateAdoptionFiles(fileList, list.length, t, lim);
    if (error) {
      message.error(error);
      return;
    }
    if (!ok.length) return;
    setBusy(true);
    try {
      await onAdd(ok);
      message.success(t("partners.deals.detail.adoptionAdded"));
    } catch (err) {
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        (err && err.message) ||
        t("partners.deals.detail.adoptionFailed");
      message.error(msg);
    } finally {
      setBusy(false);
    }
  }

  function handleRemove(file) {
    Modal.confirm({
      title: t("partners.deals.detail.adoptionDelete"),
      content: t("partners.deals.detail.adoptionDeleteConfirm", { name: file.originalName }),
      okText: t("partners.deals.detail.adoptionDelete"),
      cancelText: t("partners.deals.detail.statusChangeConfirmCancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusy(true);
        try {
          await onRemove(file);
          message.success(t("partners.deals.detail.adoptionDeleted"));
        } catch (err) {
          const msg =
            (err && err.payload && err.payload.error && err.payload.error.message) ||
            (err && err.message) ||
            t("partners.deals.detail.adoptionFailed");
          message.error(msg);
        } finally {
          setBusy(false);
        }
      },
    });
  }

  async function handleDownload(file) {
    try {
      await onDownload(file);
    } catch (err) {
      message.error((err && err.message) || t("partners.deals.detail.adoptionDownloadFailed"));
    }
  }

  // 도입 상태에서는 최소 1개 유지 — 마지막 파일 삭제 버튼 비활성
  const removeLocked = isAdoptionStage && list.length <= 1;

  return (
    <section className={`rounded-2xl border border-slate-200 bg-white p-5 md:p-7 ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
            {t("partners.deals.detail.adoptionFiles")}
          </h2>
          <p className="mt-1 px-1 text-xs text-slate-500">
            {t("partners.deals.detail.adoptionFilesHint")}
            {canManage ? "" : ` ${t("partners.deals.detail.adoptionFilesManageHint")}`}
          </p>
        </div>
        {canManage && (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current && inputRef.current.click()}
              className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:border-accent hover:text-accent disabled:opacity-60"
            >
              {busy
                ? t("partners.deals.detail.adoptionUploading")
                : t("partners.deals.detail.adoptionAdd")}
            </button>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={adoptionAccept(lim)}
              className="hidden"
              onChange={(e) => {
                handleAdd(e.target.files);
                e.target.value = "";
              }}
            />
          </>
        )}
      </div>
      {list.length === 0 ? (
        <p className="mt-3 px-1 text-sm text-slate-400">
          {t("partners.deals.detail.adoptionNone")}
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200">
          {list.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <button
                type="button"
                onClick={() => handleDownload(f)}
                className="min-w-0 flex-1 truncate text-left text-accent underline-offset-2 hover:underline"
                title={f.originalName}
              >
                {f.originalName}
              </button>
              <span className="shrink-0 text-xs text-slate-500">{formatFileSize(f.sizeBytes)}</span>
              {f.createdAt && (
                <span className="hidden shrink-0 text-xs text-slate-400 sm:inline">
                  {String(f.createdAt).slice(0, 10)}
                </span>
              )}
              {canManage && (
                <button
                  type="button"
                  disabled={busy || removeLocked}
                  title={removeLocked ? t("partners.deals.detail.adoptionMinLocked") : undefined}
                  onClick={() => handleRemove(f)}
                  className="shrink-0 text-xs text-slate-500 underline-offset-2 hover:text-rose-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t("partners.deals.detail.adoptionDelete")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
